<template>
  <BaseLayout :menuLinks="menuLinks">
    <template v-slot>
      <!-- Breadcrumb Navigation -->
      <ModernBreadcrumb :items="breadcrumbItems" />

      <!-- Standardized Header -->
      <StandardHeader
        title="User Settings"
        subtitle="Manage your profile"
        icon="pi pi-user-edit"
      />

      <div class="user-settings-page p-4">
        <div class="flex justify-content-center">
        <Card class="settings-card shadow-4 border-round-xl overflow-hidden backdrop-blur-sm">
          <template #content>
            <div class="p-fluid p-6">
              <div class="flex flex-column gap-4 mt-4">
                <div class="field">
                  <label for="name" class="block text-sm font-semibold text-gray-800 mb-2">Full Name</label>
                  <IconField iconPosition="left">
                    <InputIcon class="pi pi-user" style="color: #94a3b8;"/>
                    <InputText
                        id="name"
                        v-model="name"
                        placeholder="Enter your full name"
                        :disabled="loading"
                        class="w-full border-round-lg hover:border-blue-400 focus:border-blue-500 transition-colors duration-200"
                        style="padding: 0.75rem 0.75rem 0.75rem 2.5rem;"
                    />
                  </IconField>
                </div>

                <div class="field">
                  <label for="email" class="block text-sm font-semibold text-gray-800 mb-2">Email Address</label>
                  <IconField iconPosition="left">
                    <InputIcon class="pi pi-envelope" style="color: #94a3b8;"/>
                    <InputText
                        id="email"
                        v-model="email"
                        type="email"
                        placeholder="Your email address"
                        disabled
                        class="w-full border-round-lg bg-gray-50"
                        style="padding: 0.75rem 0.75rem 0.75rem 2.5rem;"
                    />
                  </IconField>
                  <small class="text-gray-500 text-xs">Email address cannot be modified</small>
                </div>

                <Button
                    label="Save Changes"
                    icon="pi pi-check"
                    class="p-button-primary w-full cta-button mt-2"
                    size="large"
                    :loading="loading"
                    @click="updateProfile"
                />
              </div>
            </div>
          </template>
        </Card>
        </div>
        <Toast/>
      </div>
    </template>
  </BaseLayout>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue';
import {useStore} from 'vuex';
import {useToast} from 'primevue/usetoast';
import Card from 'primevue/card';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Toast from 'primevue/toast';
import ModernBreadcrumb from '@/components/ModernBreadcrumb.vue';
import StandardHeader from '@/components/StandardHeader.vue';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import BaseLayout from '@/components/BaseLayout.vue';
import {menuLinks} from '@/shared/sharedLinks.js';

const store = useStore();
const toast = useToast();

// User data
const user = computed(() => store.getters.user);
const email = ref('');
const name = ref('');

// UI state
const loading = ref(false);

// Breadcrumb navigation
const breadcrumbItems = ref([
  { label: 'My Feeds', icon: 'pi pi-home', route: '/feeds' },
  { label: 'Account Settings', icon: 'pi pi-user-edit' }
]);

// Load user data
onMounted(() => {
  if (user.value) {
    email.value = user.value.email || '';
    name.value = user.value.name || '';
  }
});

// Update profile information
const updateProfile = async () => {
  if (loading.value) return;

  loading.value = true;

  try {
    await store.dispatch('updateUserProfile', {
      name: name.value
    });

    toast.add({
      severity: 'success',
      summary: 'Profile Updated',
      detail: 'Your profile information has been updated successfully.',
      life: 3000
    });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Update Failed',
      detail: error.message || 'Failed to update profile. Please try again.',
      life: 3000
    });
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.user-settings-page {
  min-height: 100vh;
  padding: 0.5rem;
}

.settings-card {
  width: 100%;
  max-width: 100%;
  margin: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  z-index: 10;
}

.p-fluid {
  padding: 1rem !important;
}

.flex.flex-column.gap-4 {
  gap: 0.75rem !important;
}

.field label {
  margin-bottom: 0.5rem !important;
}

.cta-button {
  position: relative;
  overflow: hidden;
  transform: translateY(0);
  transition: all 0.3s ease;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}

.cta-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.cta-button:hover::before {
  left: 100%;
}

.transition-colors {
  transition: color 0.2s ease, border-color 0.2s ease;
}

@media (min-width: 576px) {
  .user-settings-page {
    padding: 0.75rem;
  }

  .settings-card {
    max-width: 480px;
  }

  .p-fluid {
    padding: 1.25rem !important;
  }

  .flex.flex-column.gap-4 {
    gap: 1rem !important;
  }
}

@media (min-width: 768px) {
  .user-settings-page {
    padding: 1rem;
  }

  .settings-card {
    max-width: 520px;
  }

  .field label {
    margin-bottom: 0.75rem !important;
  }
}

@media (min-width: 992px) {
  .user-settings-page {
    padding: 1.5rem;
  }

  .settings-card {
    max-width: 650px;
  }

  .p-fluid {
    padding: 1.5rem !important;
  }

  .flex.flex-column.gap-4 {
    gap: 1rem !important;
  }
}
</style>